<template>
  <Transition name="fade" mode="out-in">
    <div v-if="shouldShow" :class="['loading-indicator', size, variant]">
      <div class="pill-spinner" v-html="pillIcon"></div>
      <span v-if="text" class="loading-text">{{ text }}</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'

const props = defineProps<{
  text?: string
  size?: 'small' | 'medium' | 'large'
  variant?: 'primary' | 'light' | 'dark' | 'subtle'
  delay?: number // Delay in milliseconds before showing the indicator
}>()

const shouldShow = ref(false)
let showTimeout: number | null = null

// Pill icon SVG - clean, modern pill shape
const pillIcon = computed(() => {
  const size = props.size === 'small' ? '16' : props.size === 'large' ? '24' : '20'
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <g transform="rotate(45 12 12)">
      <rect x="4" y="8" width="16" height="8" rx="4"/>
      <line x1="12" y1="8" x2="12" y2="16"/>
    </g>
  </svg>`
})

const startDelay = () => {
  if (props.delay && props.delay > 0) {
    showTimeout = window.setTimeout(() => {
      shouldShow.value = true
    }, props.delay)
  } else {
    shouldShow.value = true
  }
}

const clearDelay = () => {
  if (showTimeout) {
    clearTimeout(showTimeout)
    showTimeout = null
  }
  shouldShow.value = false
}

// Watch for changes in the delay prop
watch(() => props.delay, () => {
  clearDelay()
  startDelay()
})

onMounted(() => {
  startDelay()
})

onUnmounted(() => {
  clearDelay()
})
</script>

<style scoped>
.loading-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-color);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loading-indicator.light {
  color: #fff;
}

.loading-indicator.dark {
  color: #1a1a1a;
}

.loading-indicator.primary {
  color: var(--primary-color);
}

.loading-indicator.subtle {
  color: var(--text-secondary);
}

.pill-spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: pillSpin 1.5s ease-in-out infinite;
  color: currentColor;
}

/* Size variants */
.loading-indicator.small .pill-spinner {
  width: 16px;
  height: 16px;
}

.loading-indicator.medium .pill-spinner {
  width: 20px;
  height: 20px;
}

.loading-indicator.large .pill-spinner {
  width: 24px;
  height: 24px;
}

.loading-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: currentColor;
}

.loading-indicator.small .loading-text {
  font-size: 0.85rem;
}

.loading-indicator.large .loading-text {
  font-size: 1rem;
}

@keyframes pillSpin {
  0% { 
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
  25% { 
    transform: rotate(90deg) scale(1.1);
    opacity: 0.8;
  }
  50% { 
    transform: rotate(180deg) scale(1);
    opacity: 1;
  }
  75% { 
    transform: rotate(270deg) scale(1.1);
    opacity: 0.8;
  }
  100% { 
    transform: rotate(360deg) scale(1);
    opacity: 1;
  }
}

/* Reduced motion support - REMOVED: This was conflicting with manual reduce motion setting */
/* Motion reduction is now handled by the global .reduce-motion class */
</style> 